import { ImageTransformOptions } from '../types';
import { getRescaleConfig } from './config';

/**
 * Codifica una URL para que sea segura como parámetro
 * @param url URL a codificar
 * @returns URL codificada
 */
function encodeURL(url: string): string {
  // Verifica si la URL ya está codificada (contiene %xx)
  if (/%[0-9A-Fa-f]{2}/.test(url)) {
    // Ya está codificada, no hacer nada
    return url;
  }
  
  // Si no está codificada, codificarla
  return encodeURIComponent(url);
}

/**
 * Construye una URL para el servicio de redimensionamiento de imágenes
 * @param src URL de la imagen original
 * @param options Opciones de transformación
 * @returns URL para el servicio rescale
 */
export const buildResizeUrl = (src: string, options: ImageTransformOptions = {}): string => {
  const config = getRescaleConfig();
  const baseUrl = `${config.serverUrl}/resize`;
  
  // Determinar si la imagen es de una URL externa o un asset local
  const isExternalUrl = src.startsWith('http://') || src.startsWith('https://') || src.startsWith('//');
  const imageUrl = isExternalUrl ? src : window.location.origin + (src.startsWith('/') ? src : `/${src}`);
  
  // Construir parámetros de la URL
  const params = new URLSearchParams();
  
  // Parámetro url es obligatorio y debe estar bien codificado
  params.append('url', encodeURL(imageUrl));
  
  // Agregar opciones de transformación
  if (options.width) params.append('width', options.width.toString());
  if (options.height) params.append('height', options.height.toString());
  
  // Usar formato y calidad por defecto si no se especifican
  const format = options.format || config.defaultFormat;
  const quality = options.quality || config.defaultQuality;
  
  params.append('format', format);
  params.append('quality', quality.toString());
  
  // Agregar opciones adicionales si están definidas
  if (options.blur) params.append('blur', options.blur.toString());
  if (options.grayscale) params.append('grayscale', 'true');
  if (options.rotate) params.append('rotate', options.rotate.toString());
  if (options.flip) params.append('flip', 'true');
  if (options.flop) params.append('flop', 'true');
  if (options.sharpen) params.append('sharpen', 'true');
  
  // Eliminar parámetro anti-caché para permitir el uso correcto de caché
  // params.append('_', Date.now().toString());
  
  return `${baseUrl}?${params.toString()}`;
};

/**
 * Construye una URL para generar imágenes responsivas
 * @param src URL de la imagen original
 * @param options Opciones de transformación
 * @returns URL para el servicio de imágenes responsivas
 */
export const buildResponsiveUrl = (src: string, options: ImageTransformOptions = {}): string => {
  const config = getRescaleConfig();
  const baseUrl = `${config.serverUrl}/responsive`;
  
  // Determinar si la imagen es de una URL externa o un asset local
  const isExternalUrl = src.startsWith('http://') || src.startsWith('https://') || src.startsWith('//');
  const imageUrl = isExternalUrl ? src : window.location.origin + (src.startsWith('/') ? src : `/${src}`);
  
  // Construir parámetros de la URL
  const params = new URLSearchParams();
  
  // Parámetro url es obligatorio y debe estar bien codificado
  params.append('url', encodeURL(imageUrl));
  
  // Usar formato y calidad por defecto si no se especifican
  const format = options.format || config.defaultFormat;
  const quality = options.quality || config.defaultQuality;
  
  params.append('format', format);
  params.append('quality', quality.toString());
  
  // Agregar opciones adicionales si están definidas
  if (options.blur) params.append('blur', options.blur.toString());
  if (options.grayscale) params.append('grayscale', 'true');
  if (options.sharpen) params.append('sharpen', 'true');
  
  // Eliminar parámetro anti-caché para permitir el uso correcto de caché
  // params.append('_', Date.now().toString());
  
  return `${baseUrl}?${params.toString()}`;
};

/**
 * Construye una URL para obtener información de una imagen
 * @param src URL de la imagen original
 * @returns URL para el servicio de información de imágenes
 */
export const buildInfoUrl = (src: string): string => {
  const config = getRescaleConfig();
  const baseUrl = `${config.serverUrl}/info`;
  
  // Determinar si la imagen es de una URL externa o un asset local
  const isExternalUrl = src.startsWith('http://') || src.startsWith('https://') || src.startsWith('//');
  const imageUrl = isExternalUrl ? src : window.location.origin + (src.startsWith('/') ? src : `/${src}`);
  
  // Construir parámetros de la URL
  const params = new URLSearchParams();
  
  // Parámetro url es obligatorio y debe estar bien codificado
  params.append('url', encodeURL(imageUrl));
  
  return `${baseUrl}?${params.toString()}`;
}; 